/**
 * P164-R5: which printable government forms belong to a round, per country.
 *
 * Owner: "ต้องพิมพ์ printform ออกมาได้ด้วยครับ จากข้อมูลที่มีครับ ... โดยเอกสารจะ
 * เปลี่ยนไปตามประเทศนั้นๆ เพื่อนำไปยื่นให้กับหน่วยงานราชการครับ".
 *
 * WHAT THESE FORMS ARE. Each one is a real form out of the owner's own
 * แบบฟอร์ม MOU MYANMAR.pdf, laid out again as printable HTML - แบบ นจ. 2
 * (page 1), หนังสือมอบอำนาจ / Power of Attorney (page 9) and แบบ บต. 46
 * (page 10). They are printed to be filled in and filed, so every field the
 * round does NOT hold is left as a real dotted blank rather than invented. That
 * is the honest shape for this feature: the system fills what it knows and the
 * operator completes the rest in pen, exactly as they do today.
 *
 * WHY THE PACK LIVES HERE AND NOT ON THE SERVER, unlike the eight steps. A step
 * is process state - which one a round is on, what it needs, when it was done -
 * so it belongs to the server and mouCaseShared.js deliberately keeps no copy.
 * A form is a page layout. It cannot be expressed as a row, and the data it
 * prints all comes from the server already. What varies per country here is
 * only which layouts are in the pack, so that list sits beside the layouts.
 *
 * WHY THE OVERRIDE MAP IS EMPTY. Owner: "ตอนนี้ทำให้แต่ละประเทศมีขั้นตอนที่
 * เหมือนกันก่อนครับ ... แต่รองรับการเปลี่ยนแปลงในอนาคตนะครับ เขียนอย่า tight".
 * All five countries file the same three forms today, so all five resolve to
 * the same pack through one shared default - and the day Bangladesh needs a
 * fourth form, that is one entry in COUNTRY_SPECIFIC_PACKS and nothing else
 * moves. This mirrors MouCaseSteps.CountrySpecificSequences on the backend,
 * which is empty for exactly the same reason.
 */

export const FORM_NOR_JOR_2 = "NorJor2";
export const FORM_POWER_OF_ATTORNEY = "PowerOfAttorney";
export const FORM_BOR_TOR_46 = "BorTor46";

/**
 * The three forms, in the order an office receives them: the application, the
 * authority to file it, and the certification that backs it.
 */
export const MOU_FORMS = [
  {
    key: FORM_NOR_JOR_2,
    code: "แบบ นจ. ๒",
    title: "คำร้องขอนำคนต่างด้าวมาทำงานกับนายจ้างในประเทศ",
    englishTitle: "Application Form for bringing foreign worker to work with employer in the Kingdom of Thailand",
  },
  {
    key: FORM_POWER_OF_ATTORNEY,
    code: "หนังสือมอบอำนาจ",
    title: "หนังสือมอบอำนาจ",
    englishTitle: "Power of Attorney",
  },
  {
    key: FORM_BOR_TOR_46,
    code: "แบบ บต. ๔๖",
    title: "หนังสือรับรองการจ้าง",
    englishTitle: "Form WP. 46 — Employment Certification",
  },
];

/** Every country files these today. */
const SHARED_PACK = [FORM_NOR_JOR_2, FORM_POWER_OF_ATTORNEY, FORM_BOR_TOR_46];

/**
 * Deliberately empty. A country whose pack genuinely differs gets an entry
 * here; until one does, adding country keys that all repeat SHARED_PACK would
 * be five copies of one fact waiting to fall out of step.
 */
const COUNTRY_SPECIFIC_PACKS = {};

/** The forms one country files, in order. */
export function formsForCountry(countryCode) {
  const keys = COUNTRY_SPECIFIC_PACKS[String(countryCode ?? "").toUpperCase()] ?? SHARED_PACK;
  return MOU_FORMS.filter((form) => keys.includes(form.key));
}

/**
 * The four job types แบบ นจ. 2 prints as its own tick-boxes, and the free-text
 * "งานอื่นๆ (ระบุ)" line it falls through to.
 *
 * A round stores jobTypeLabel as free text, so this matches it against the
 * form's own wording rather than the other way round: whatever was typed, the
 * form still has to be ticked in one of exactly four places. Anything that is
 * not one of the three named jobs is "other", printed with what was actually
 * typed beside it - which is what the paper form asks for.
 */
export const NOR_JOR_2_JOB_ROWS = [
  { key: "labourer", thai: "งานกรรมกร", english: "Labourer", matches: ["กรรมกร", "labour", "labor"] },
  { key: "domestic", thai: "งานรับใช้ในบ้าน", english: "Domestic Worker", matches: ["รับใช้ในบ้าน", "แม่บ้าน", "domestic"] },
  {
    key: "mechanic",
    thai: "งานช่างเครื่องยนต์ในเรือประมงทะเล",
    english: "Mechanic on Fishing vessel",
    matches: ["ช่างเครื่องยนต์", "ประมง", "fishing", "mechanic"],
  },
];

/**
 * Which tick-box a round's job type falls in, and what to print on the
 * "อื่นๆ (ระบุ)" line when it falls through. Never guesses: an empty job type
 * ticks nothing at all, because a form filed with the wrong box ticked is worse
 * than one filed with the box blank.
 */
export function resolveJobRow(jobTypeLabel) {
  const typed = String(jobTypeLabel ?? "").trim();
  if (!typed) {
    return { key: null, otherText: null };
  }

  const lowered = typed.toLowerCase();
  const row = NOR_JOR_2_JOB_ROWS.find((candidate) =>
    candidate.matches.some((needle) => lowered.includes(needle.toLowerCase()))
  );

  return row ? { key: row.key, otherText: null } : { key: "other", otherText: typed };
}

/**
 * Thai numerals, which is what the government forms use for their own numbering
 * (แบบ นจ. ๒, ข้อ ๑ ... ข้อ ๑๑). Applied only to the form's own furniture, never
 * to a value: a head count or a licence number printed in Thai digits would be
 * harder to check against the source record, not easier.
 */
const THAI_DIGITS = ["๐", "๑", "๒", "๓", "๔", "๕", "๖", "๗", "๘", "๙"];

export function thaiNumeral(value) {
  return String(value)
    .split("")
    .map((character) => (character >= "0" && character <= "9" ? THAI_DIGITS[Number(character)] : character))
    .join("");
}

/**
 * The Buddhist-era date an operator writes on a Thai government form, from a
 * real Date. Printed as the day the form was produced - these forms carry a
 * ลงวันที่ line, and leaving today's date blank when the machine plainly knows
 * it just makes more handwriting.
 */
export function thaiFormDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return { day: "", month: "", year: "" };
  }
  const months = [
    "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
    "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
  ];
  return {
    day: String(date.getDate()),
    month: months[date.getMonth()],
    year: String(date.getFullYear() + 543),
  };
}
